/**
 * Maps patient workflow statuses to medonc-style badge dropdown options.
 */

import type { BadgeWithIconProps } from "../data-model/badgeWithIconProps";
import {
  DEFAULT_PATIENT_STATUS_BADGE_STYLE,
  PATIENT_STATUS_BADGE_STYLES,
  PatientStatus,
} from "../enums/patientStatus";

/** One option from `GET /eligibility/options` `patient_status`. */
export interface PatientStatusOption {
  value: string;
  label: string;
}

/**
 * Resolve badge colors for a stored patient status value.
 *
 * @param status - Persisted patient status from the eligibility API
 * @returns Foreground and background colors for the badge
 */
export function getPatientStatusBadgeStyle(status: string): {
  textColor: string;
  bgColor: string;
} {
  const canonical = Object.values(PatientStatus).find(
    (value) => value === status,
  );
  if (!canonical) {
    return DEFAULT_PATIENT_STATUS_BADGE_STYLE;
  }
  return PATIENT_STATUS_BADGE_STYLES[canonical];
}

/**
 * Build a single badge descriptor for the current patient status.
 *
 * @param status - Persisted patient status
 * @returns Badge props for {@link BadgeDropdown}
 */
export function toPatientStatusBadgeProps(status: string): BadgeWithIconProps {
  const { textColor, bgColor } = getPatientStatusBadgeStyle(status);
  return {
    id: status,
    text: status,
    textColor,
    bgColor,
  };
}

/**
 * Build the full patient-status dropdown list with design colors applied.
 *
 * @param options - Server-served patient status options; falls back to the enum
 * @returns Badge list for {@link BadgeDropdown}
 */
export function buildPatientStatusBadgeOptions(
  options: PatientStatusOption[],
): BadgeWithIconProps[] {
  const source =
    options.length > 0
      ? options
      : Object.values(PatientStatus).map((value) => ({
          value,
          label: value,
        }));

  return source.map((option) => {
    const { textColor, bgColor } = getPatientStatusBadgeStyle(option.value);
    return {
      id: option.value,
      text: option.label || option.value,
      textColor,
      bgColor,
    };
  });
}
